import { OrientationLockType, ScreenOrientation } from "@capacitor/screen-orientation";
import { Box, makeStyles, Typography } from "@material-ui/core";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router";

import {
  WebinarDetail,
  WebinarDetailVideoTypeEnum,
  WebinarSummary,
  WebinarViewingCreateRequest,
  WebinarViewingUpdateRequestEventTypeEnum,
} from "@/api/generated";
import { CustomButton } from "@/components/Button";
import {
  ExclusiveFetchError,
  ExclusivePageHeading,
  ExclusiveTemplate,
  LearningContentLink,
  useExclusiveLayout,
} from "@/components/exclusive/ExclusiveContentLayout";
import { ExclusiveVideoCard } from "@/components/exclusive/ExclusiveVideoCard";
import { Loading } from "@/components/Loading";
import { layoutTokens as layout, semanticColors as colors, textStyles } from "@/designTokens";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import theme from "@/theme";
import { isAndroid } from "@/utilities/capacitorLogic";

import { ExternalVideoAnchor } from "./components/ExternalVideoAnchor";
import { PlaybackSnapshot, WebinarViewingTracker } from "./components/logics/viewingTracker";
import { isRecoverableYoutubeError } from "./components/logics/youtubePlayerPage";
import { YoutubePlayer, YoutubePlayerErrorCause } from "./components/YoutubePlayer";

type PremiumWebinarDetailParams = {
  id: string;
};

const RELATED_VIDEOS_DISPLAY_LIMIT = 10;
const RELATED_FETCH_SIZE = RELATED_VIDEOS_DISPLAY_LIMIT + 1;

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  mozFullScreenElement?: Element | null;
  msFullscreenElement?: Element | null;
  webkitIsFullScreen?: boolean;
};

type FullscreenVideoElement = HTMLVideoElement & {
  webkitDisplayingFullscreen?: boolean;
};

const useStyles = makeStyles({
  root: { [theme.breakpoints.down("sm")]: { padding: 0 } },
  columns: {
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("md")]: {
      display: "grid",
      gridTemplateColumns: "minmax(0,384fr) minmax(0,776fr)",
      gap: layout.space[40],
      alignItems: "start",
    },
  },
  main: { minWidth: 0, [theme.breakpoints.up("md")]: { gridColumn: 2, gridRow: 1 } },
  side: {
    padding: layout.space[24],
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: layout.space[24],
    [theme.breakpoints.up("md")]: { padding: 0, gridColumn: 1, gridRow: 1 },
  },
  videoWrapper: {
    position: "relative",
    width: "100%",
    aspectRatio: "16 / 9",
    backgroundColor: colors["surface/inverse"],
    overflow: "hidden",
    [theme.breakpoints.up("md")]: { borderRadius: layout.radius.lg },
  },
  video: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    border: "none",
    backgroundColor: colors["surface/inverse"],
  },
  failure: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: layout.space[24],
    gap: layout.space[12],
    textAlign: "center",
    color: colors["text/inverse"],
    overflowY: "auto",
  },
  failureTitle: { ...textStyles["SP/Button/M"] },
  reason: { ...textStyles["SP/Body/Small"] },
  failureButtons: {
    display: "flex",
    justifyContent: "center",
    gap: layout.space[12],
    width: "100%",
    flexWrap: "wrap",
    "& > *": { width: "auto" },
  },
  fallbackButton: { width: "auto", minWidth: 136, [theme.breakpoints.up("md")]: { minWidth: 240 } },
  spHead: {
    padding: `${layout.space[16]}px ${layout.space[24]}px 0`,
    display: "flex",
    flexDirection: "column",
    gap: layout.space[8],
    [theme.breakpoints.up("md")]: { display: "none" },
  },
  title: { ...textStyles["SP/Heading/H3"], overflowWrap: "anywhere" },
  description: {
    padding: `${layout.space[8]}px ${layout.space[24]}px 0`,
    ...textStyles["SP/Body/Large"],
    whiteSpace: "pre-line",
    overflowWrap: "anywhere",
    [theme.breakpoints.up("md")]: {
      padding: layout.space[24],
      backgroundColor: colors["surface/card"],
      borderRadius: layout.radius.lg,
      marginTop: layout.space[24],
    },
  },
  related: { display: "flex", flexDirection: "column", gap: layout.space[12], minWidth: 0 },
});

export const PremiumWebinarDetailPage: React.FC = () => {
  const classes = useStyles();
  const common = useExclusiveLayout();
  const { id } = useParams<PremiumWebinarDetailParams>();

  const api = useMemo(() => PropallyApiFactory(), []);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const orientationLockRef = useRef<OrientationLockType | null>(null);
  const youtubePlayingRef = useRef(false);
  const history = useHistory();

  // 視聴ログの時間追跡・送信ロジック（HTML5 video / YouTube 共通）。id が変わると作り直され、内部状態もリセットされる
  const tracker = useMemo(
    () =>
      new WebinarViewingTracker({
        createViewing: async () => {
          if (!id) {
            return null;
          }
          const payload: WebinarViewingCreateRequest = {};
          const response = await api.v1WebinarsIdViewHistoriesPost(id, payload, { withCredentials: true });
          return response.data.viewing_history?.id ?? null;
        },
        updateViewing: (viewingId, payload) =>
          api.v1WebinarsIdViewHistoriesViewingIdPatch(id, viewingId, payload, { withCredentials: true }),
      }),
    [api, id]
  );

  const [webinar, setWebinar] = useState<WebinarDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailRetry, setDetailRetry] = useState(0);
  const [relatedRetry, setRelatedRetry] = useState(0);
  const [relatedError, setRelatedError] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [error, setError] = useState(false);
  const [relatedWebinars, setRelatedWebinars] = useState<WebinarSummary[]>([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  // YouTube プレイヤーのエラー表示区分（再試行はプレイヤーの再マウントで行う。tracker には影響しない）。
  // "load" = 回復可能（通信起因のロード失敗や一時的な再生エラー。再試行導線を出す）、
  // "playback" = 非回復（コード 100/101/150/152/153。YouTube 本体への外部リンクを出す）。
  // 区分は handleYoutubeError がエラーコードで決める
  const [youtubeError, setYoutubeError] = useState<YoutubePlayerErrorCause | null>(null);
  const [youtubePlayerKey, setYoutubePlayerKey] = useState(0);

  const lockOrientation = useCallback(async (orientation: OrientationLockType) => {
    if (!isAndroid) {
      return;
    }
    if (orientationLockRef.current === orientation) {
      return;
    }
    try {
      await ScreenOrientation.lock({ orientation });
      orientationLockRef.current = orientation;
    } catch (lockError) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("画面回転のロックに失敗しました", lockError);
      }
    }
  }, []);

  const lockPortrait = useCallback(() => {
    void lockOrientation("portrait");
  }, [lockOrientation]);

  const lockLandscape = useCallback(() => {
    void lockOrientation("landscape");
  }, [lockOrientation]);

  const unlockOrientation = useCallback(async () => {
    if (!isAndroid) {
      return;
    }
    try {
      await ScreenOrientation.unlock();
      orientationLockRef.current = null;
    } catch (unlockError) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("画面回転ロック解除に失敗しました", unlockError);
      }
    }
  }, []);

  /**
   * 離脱・非表示時の視聴時間積算に使う再生状態のスナップショット。
   * s3 (<video>) は要素の paused / seeking を、YouTube はプレイヤーの再生中フラグを参照する。
   */
  const getPlaybackSnapshot = useCallback((): PlaybackSnapshot | null => {
    const video = videoRef.current;
    if (video) {
      return { paused: video.paused, seeking: video.seeking };
    }
    return { paused: !youtubePlayingRef.current, seeking: false };
  }, []);

  // ---- HTML5 <video>（s3 動画）用イベントハンドラ ----

  const handlePlay = useCallback(() => {
    tracker.handlePlay();
  }, [tracker]);

  const handleTimeUpdate = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      const video = event.currentTarget;
      tracker.handleTimeUpdate(video.currentTime, { paused: video.paused, seeking: video.seeking });
    },
    [tracker]
  );

  const handlePause = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      const video = event.currentTarget;
      tracker.handlePause(video.currentTime, { paused: video.paused, seeking: video.seeking });
    },
    [tracker]
  );

  const handleEnded = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      const video = event.currentTarget;
      tracker.handleEnded(video.currentTime, video.duration, { paused: video.paused, seeking: video.seeking });
    },
    [tracker]
  );

  const handleSeeked = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      const video = event.currentTarget;
      tracker.handleSeeked(video.currentTime, { paused: video.paused, seeking: video.seeking });
    },
    [tracker]
  );

  // ---- YouTube プレイヤー用イベントハンドラ ----

  const handleYoutubePlay = useCallback(() => {
    tracker.handlePlay();
  }, [tracker]);

  const handleYoutubeProgress = useCallback(
    (currentTimeSeconds: number) => {
      tracker.handleTimeUpdate(currentTimeSeconds, { paused: false, seeking: false });
    },
    [tracker]
  );

  const handleYoutubePause = useCallback(
    (currentTimeSeconds: number) => {
      tracker.handlePause(currentTimeSeconds, { paused: true, seeking: false });
    },
    [tracker]
  );

  const handleYoutubeEnded = useCallback(
    (currentTimeSeconds: number, durationSeconds?: number) => {
      tracker.handleEnded(currentTimeSeconds, durationSeconds, { paused: true, seeking: false });
    },
    [tracker]
  );

  const handleYoutubePlayingChange = useCallback((isPlaying: boolean) => {
    youtubePlayingRef.current = isPlaying;
  }, []);

  const handleYoutubeError = useCallback(
    (cause: YoutubePlayerErrorCause, errorCode?: number) => {
      youtubePlayingRef.current = false;
      // 回復可能かどうかの判定（コードの意味論）は logics/youtubePlayerPage.ts に集約している
      if (cause === "playback" && !isRecoverableYoutubeError(errorCode)) {
        // 再試行で回復しない再生不可（埋め込み禁止・削除済み・埋め込み元を識別できない）は運用で気づけるよう Sentry に残す。
        // console.error は CaptureConsole（src/index.tsx）経由で Sentry へ送られるため、captureMessage は使わず一本化する。
        // 第1引数は固定文言（グルーピング維持）、対象特定用の ID 群は後続引数で渡す
        console.error("ウェビナーの YouTube 動画が再生できません（埋め込み禁止・削除済み・埋め込み元不正の可能性）", {
          webinarId: id,
          youtubeVideoId: webinar?.youtube_video_id,
          errorCode,
        });
        setYoutubeError("playback");
        return;
      }
      // 上記以外（load 失敗、playback のコード 2/5/不明/undefined）は一時的な失敗として
      // 再試行導線を出す。Sentry には送らない
      setYoutubeError("load");
    },
    [id, webinar?.youtube_video_id]
  );

  const handleYoutubeRetry = useCallback(() => {
    setYoutubeError(null);
    // key を変えてプレイヤーを再マウントし、IFrame API のロードからやり直す
    setYoutubePlayerKey((previousKey) => previousKey + 1);
  }, []);

  const handleRelatedNavigate = useCallback(
    (webinarId: string) => {
      history.push(`/webinars/${webinarId}`);
    },
    [history]
  );

  const handleBack = useCallback(() => {
    if (history.length > 1) {
      history.goBack();
      return;
    }
    history.replace("/webinars");
  }, [history]);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      history.replace("/webinars");
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [history]);

  useEffect(() => {
    // 視聴ログ関連の状態は tracker が id ごとに作り直されるためここではリセットしない
    orientationLockRef.current = null;
    youtubePlayingRef.current = false;
    setYoutubeError(null);
    setVideoError(false);
  }, [id]);

  useEffect(() => {
    if (!id) {
      return;
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        tracker.flush(WebinarViewingUpdateRequestEventTypeEnum.Hidden, getPlaybackSnapshot());
      }
    };
    const handlePageHide = () => {
      tracker.flush(WebinarViewingUpdateRequestEventTypeEnum.Manual, getPlaybackSnapshot());
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
      tracker.flush(WebinarViewingUpdateRequestEventTypeEnum.Manual, getPlaybackSnapshot());
    };
  }, [getPlaybackSnapshot, id, tracker]);

  useEffect(() => {
    void unlockOrientation();
    return () => {
      lockPortrait();
    };
  }, [lockPortrait, unlockOrientation]);

  const ensureFullscreenOrientation = useCallback(() => {
    const video = videoRef.current as FullscreenVideoElement | null;
    const doc = document as FullscreenDocument;
    const isFullscreen =
      !!doc.fullscreenElement ||
      !!doc.webkitFullscreenElement ||
      !!doc.mozFullScreenElement ||
      !!doc.msFullscreenElement ||
      video?.matches?.(":fullscreen") ||
      video?.matches?.(":-webkit-full-screen") ||
      !!video?.webkitDisplayingFullscreen ||
      !!doc.webkitIsFullScreen;
    if (isFullscreen) {
      setTimeout(() => lockLandscape(), 0);
    } else {
      void unlockOrientation();
    }
  }, [lockLandscape, unlockOrientation]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const handleFullscreenChange = () => {
      ensureFullscreenOrientation();
    };

    const fullscreenEvents = [
      "fullscreenchange",
      "webkitfullscreenchange",
      "mozfullscreenchange",
      "MSFullscreenChange",
    ];
    const targets: EventTarget[] = [document, video];
    targets.forEach((target) => {
      fullscreenEvents.forEach((eventName) => target.addEventListener(eventName, handleFullscreenChange));
    });

    // iOS Safari のネイティブフルスクリーン用イベントは残す
    const handleWebkitBeginFullscreen = () => lockLandscape();
    const handleWebkitEndFullscreen = () => {
      void unlockOrientation();
    };
    video.addEventListener("webkitbeginfullscreen", handleWebkitBeginFullscreen as EventListener);
    video.addEventListener("webkitendfullscreen", handleWebkitEndFullscreen as EventListener);

    return () => {
      targets.forEach((target) => {
        fullscreenEvents.forEach((eventName) => target.removeEventListener(eventName, handleFullscreenChange));
      });
      video.removeEventListener("webkitbeginfullscreen", handleWebkitBeginFullscreen as EventListener);
      video.removeEventListener("webkitendfullscreen", handleWebkitEndFullscreen as EventListener);
    };
  }, [ensureFullscreenOrientation, lockLandscape, unlockOrientation, webinar?.id, videoError]);

  useEffect(() => {
    let cancelled = false;
    const fetchDetail = async () => {
      setLoading(true);
      setError(false);
      setWebinar(null);
      try {
        if (!id) {
          if (!cancelled) setError(true);
          return;
        }
        const response = await api.v1WebinarsIdGet(id, { withCredentials: true });
        if (!cancelled) {
          const data = response.data.webinar ?? null;
          setWebinar(data);
          setError(!data);
        }
      } catch (fetchError) {
        console.error(fetchError);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [api, id, detailRetry]);

  useEffect(() => {
    if (!webinar?.id) {
      setRelatedWebinars([]);
      setRelatedLoading(false);
      setRelatedError(false);
      return;
    }
    let isCancelled = false;
    const fetchRelated = async () => {
      try {
        setRelatedLoading(true);
        setRelatedError(false);
        const response = await api.v1WebinarsGet(1, RELATED_FETCH_SIZE, true, {
          withCredentials: true,
        });
        const items = (response.data.webinars ?? [])
          .filter((item): item is WebinarSummary & { id: string } => Boolean(item?.id) && item.id !== webinar?.id)
          .slice(0, RELATED_VIDEOS_DISPLAY_LIMIT);
        if (!isCancelled) {
          setRelatedWebinars(items);
        }
      } catch (relatedError) {
        console.error("関連ウェビナーの取得に失敗しました", relatedError);
        if (!isCancelled) {
          setRelatedError(true);
        }
      } finally {
        if (!isCancelled) {
          setRelatedLoading(false);
        }
      }
    };

    fetchRelated();
    return () => {
      isCancelled = true;
    };
  }, [api, webinar?.id, relatedRetry]);

  const published =
    webinar?.published_at && Number.isFinite(new Date(webinar.published_at).getTime())
      ? `${new Date(webinar.published_at).toLocaleDateString()} 公開`
      : undefined;
  const playerFailure = (title: string, reason: string, retry?: () => void, youtube = false) => (
    <Box className={classes.failure} role="alert">
      <Typography className={classes.failureTitle}>{title}</Typography>
      <Typography className={classes.reason}>{reason}</Typography>
      <Box className={classes.failureButtons}>
        {retry && (
          <CustomButton buttonSize="M" className={classes.fallbackButton} tone="inverse" onClick={retry}>
            再試行
          </CustomButton>
        )}
        {youtube && webinar?.youtube_video_id && (
          <CustomButton
            buttonSize="M"
            className={classes.fallbackButton}
            tone="inverse"
            component={ExternalVideoAnchor}
            href={`https://www.youtube.com/watch?v=${webinar.youtube_video_id}`}
          >
            YouTubeで見る
          </CustomButton>
        )}
      </Box>
    </Box>
  );
  return (
    <ExclusiveTemplate headerTitle="プロパリー限定動画" onBack={handleBack} isDense isPcV2 disableBottomPadding>
      <Box className={`${common.body} ${classes.root}`}>
        <ExclusivePageHeading
          title={webinar?.title || "プロパリー限定動画"}
          backLabel="プロパリー限定動画"
          onBack={handleBack}
          subtitle={published}
        />
        {loading ? (
          <Box p={3}>
            <Loading />
          </Box>
        ) : error || !webinar ? (
          <Box p={3}>
            <ExclusiveFetchError
              message="動画情報の読み込みに失敗しました。"
              onRetry={() => setDetailRetry((value) => value + 1)}
            />
          </Box>
        ) : (
          <Box className={classes.columns}>
            <Box className={classes.main}>
              <Box className={classes.videoWrapper}>
                {webinar.video_type === WebinarDetailVideoTypeEnum.Youtube ? (
                  webinar.youtube_video_id ? (
                    youtubeError === "playback" ? (
                      playerFailure(
                        "この動画はアプリ内で再生できません",
                        "この動画は現在再生できません。削除・非公開になったか、埋め込みが許可されていない可能性があります。",
                        undefined,
                        true
                      )
                    ) : youtubeError === "load" ? (
                      playerFailure(
                        "動画を読み込めませんでした",
                        "動画の読み込みに失敗しました。通信環境をご確認のうえ、再試行してください。",
                        handleYoutubeRetry,
                        true
                      )
                    ) : (
                      <YoutubePlayer
                        key={youtubePlayerKey}
                        videoId={webinar.youtube_video_id}
                        className={classes.video}
                        onPlay={handleYoutubePlay}
                        onProgress={handleYoutubeProgress}
                        onPause={handleYoutubePause}
                        onEnded={handleYoutubeEnded}
                        onPlayingChange={handleYoutubePlayingChange}
                        onError={handleYoutubeError}
                      />
                    )
                  ) : (
                    playerFailure("動画を再生できません", "動画の再生URLが設定されていません。")
                  )
                ) : webinar.video_url ? (
                  videoError ? (
                    playerFailure(
                      "動画を読み込めませんでした",
                      "動画の読み込みに失敗しました。通信環境をご確認のうえ、再試行してください。",
                      () => {
                        setVideoError(false);
                        setDetailRetry((value) => value + 1);
                      }
                    )
                  ) : (
                    <video
                      ref={videoRef}
                      className={classes.video}
                      src={webinar.video_url}
                      controls
                      controlsList="nodownload"
                      playsInline
                      preload="metadata"
                      poster={webinar.thumbnail_url}
                      onPlay={handlePlay}
                      onTimeUpdate={handleTimeUpdate}
                      onPause={handlePause}
                      onEnded={handleEnded}
                      onSeeked={handleSeeked}
                      onError={() => setVideoError(true)}
                    />
                  )
                ) : (
                  playerFailure("動画を再生できません", "動画の再生URLが設定されていません。")
                )}
              </Box>
              <Box className={classes.spHead}>
                <Typography component="h1" className={classes.title}>
                  {webinar.title}
                </Typography>
                {published && <Typography className={common.small}>{published}</Typography>}
              </Box>
              {webinar.description && (
                <Typography component="div" className={classes.description}>
                  {webinar.description}
                </Typography>
              )}
            </Box>
            <Box className={classes.side}>
              {(relatedLoading || relatedError || relatedWebinars.length > 0) && (
                <Box component="section" aria-label="次に見る" className={classes.related}>
                  <Typography component="h2" className={common.sectionTitle}>
                    次に見る
                  </Typography>
                  {relatedError && (
                    <ExclusiveFetchError
                      message="次に見る動画の読み込みに失敗しました。"
                      onRetry={() => setRelatedRetry((value) => value + 1)}
                    />
                  )}
                  {relatedLoading && <Loading />}
                  {relatedWebinars.map((item, index) => (
                    <ExclusiveVideoCard
                      key={item.id ?? `related-${index}`}
                      webinar={item}
                      variant="next"
                      onClick={() => item.id && handleRelatedNavigate(item.id)}
                    />
                  ))}
                </Box>
              )}
              <LearningContentLink />
            </Box>
          </Box>
        )}
      </Box>
    </ExclusiveTemplate>
  );
};
